import axios from 'axios';
import { useCallback, useState } from 'react';

export type DocumentOpenStatus =
  | 'idle'
  | 'loading'
  | 'success'
  | 'not-found'
  | 'blocked'
  | 'error';

export type DocumentOpenOutcome = Exclude<
  DocumentOpenStatus,
  'idle' | 'loading'
>;

const PDF_MEDIA_TYPE = 'application/pdf';

function asPdf(blob: Blob): Blob {
  return blob.type === PDF_MEDIA_TYPE
    ? blob
    : new Blob([blob], { type: PDF_MEDIA_TYPE });
}

export function useDocumentOpen(load: () => Promise<Blob>) {
  const [status, setStatus] = useState<DocumentOpenStatus>('idle');

  const open = useCallback(async (): Promise<DocumentOpenOutcome> => {
    const tab = window.open('', '_blank');
    if (!tab) {
      setStatus('blocked');
      return 'blocked';
    }

    tab.opener = null;
    setStatus('loading');
    try {
      const url = URL.createObjectURL(asPdf(await load()));
      tab.location.href = url;
      window.addEventListener('pagehide', () => URL.revokeObjectURL(url), {
        once: true,
      });
      setStatus('success');
      return 'success';
    } catch (error) {
      tab.close();
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        setStatus('not-found');
        return 'not-found';
      }

      console.error('Failed to open the document', error);
      setStatus('error');
      return 'error';
    }
  }, [load]);

  return { status, open };
}
